import React, { useEffect, useState } from 'react';
import {
  getAnalyticsKpis, getTendances, getPrevisionsVentes, getPrevisionsStock,
  getAnomalies, getSaisonnalite, getRecommandations, getStations
} from '../services/api';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ReferenceLine, Cell
} from 'recharts';

const fmt = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n));
const COLORS = ['#ED0000', '#FF6B35', '#FFC72C', '#78BE20', '#004A97', '#8B5CF6', '#EC4899'];
const MOIS_LABELS = ['Jan','Fev','Mar','Avr','Mai','Jun','Jul','Aou','Sep','Oct','Nov','Dec'];

const PRIO_COLORS: Record<string, string> = { haute: '#ED0000', moyenne: '#FF6B35', basse: '#78BE20' };
const CAT_ICONS: Record<string, string> = { stock: '📦', controle: '🔍', performance: '📉', budget: '💰', saisie: '📝' };

const Analytics: React.FC = () => {
  const [tab, setTab] = useState<'overview' | 'previsions' | 'stock' | 'anomalies' | 'saisonnalite' | 'recommandations'>('overview');
  const [stations, setStations] = useState<any[]>([]);
  const [stationId, setStationId] = useState('');
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [mois, setMois] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);

  // Data states
  const [kpis, setKpis] = useState<any>(null);
  const [tendances, setTendances] = useState<any>(null);
  const [previsions, setPrevisions] = useState<any>(null);
  const [stockPrev, setStockPrev] = useState<any[]>([]);
  const [anomalies, setAnomalies] = useState<any>(null);
  const [saison, setSaison] = useState<any>(null);
  const [recos, setRecos] = useState<any>(null);

  useEffect(() => { getStations().then(r => setStations(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadData(); }, [tab, stationId, annee, mois]);

  const loadData = async () => {
    setLoading(true);
    const p: any = { annee: String(annee), mois: String(mois) };
    if (stationId) p.station_id = stationId;
    try {
      if (tab === 'overview') {
        const [k, t] = await Promise.all([getAnalyticsKpis(p), getTendances(p)]);
        setKpis(k.data); setTendances(t.data);
      }
      if (tab === 'previsions') setPrevisions((await getPrevisionsVentes(p)).data);
      if (tab === 'stock') setStockPrev((await getPrevisionsStock(p)).data);
      if (tab === 'anomalies') setAnomalies((await getAnomalies(p)).data);
      if (tab === 'saisonnalite') setSaison((await getSaisonnalite(p)).data);
      if (tab === 'recommandations') setRecos((await getRecommandations(p)).data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const renderKpiCard = (label: string, value: string, sub?: string, color?: string, variation?: number) => (
    <div className="stat-card" style={{ borderTop: color ? `4px solid ${color}` : undefined }}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {variation !== undefined && (
        <div style={{ fontSize: 12, marginTop: 4, color: variation >= 0 ? '#78BE20' : '#e53935', fontWeight: 600 }}>
          {variation >= 0 ? '▲' : '▼'} {Math.abs(variation)}%
        </div>
      )}
      {sub && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h2>Analytics & Predictions</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={stationId} onChange={e => setStationId(e.target.value)} className="form-control" style={{ width: 180 }}>
            <option value="">Toutes les stations</option>
            {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
          <select value={mois} onChange={e => setMois(Number(e.target.value))} className="form-control" style={{ width: 90 }}>
            {MOIS_LABELS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={annee} onChange={e => setAnnee(Number(e.target.value))} className="form-control" style={{ width: 80 }}>
            {[2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      <div className="tabs" style={{ flexWrap: 'wrap' }}>
        {[
          { key: 'overview', label: "Vue d'ensemble" },
          { key: 'previsions', label: 'Previsions Ventes' },
          { key: 'stock', label: 'Previsions Stock' },
          { key: 'anomalies', label: 'Anomalies' },
          { key: 'saisonnalite', label: 'Saisonnalite' },
          { key: 'recommandations', label: 'Recommandations' },
        ].map(t => (
          <button key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key as any)}>{t.label}</button>
        ))}
      </div>

      {loading ? <div className="loading">Chargement...</div> : (
        <>
          {/* ===== VUE D'ENSEMBLE ===== */}
          {tab === 'overview' && kpis && tendances && (
            <>
              <div className="stats-grid">
                {renderKpiCard('CA du mois', `${fmt(kpis.ca_mois_courant)} FCFA`, `vs ${fmt(kpis.ca_mois_precedent)} mois prec.`, '#ED0000', kpis.variation_ca_pct)}
                {renderKpiCard('Volume du mois', `${fmt(kpis.volume_mois_courant)} L`, `vs ${fmt(kpis.volume_mois_precedent)} mois prec.`, '#004A97', kpis.variation_volume_pct)}
                {renderKpiCard('Marge brute', `${fmt(kpis.marge_brute)} FCFA`, `Taux: ${kpis.taux_marge_pct}%`, '#78BE20')}
                {renderKpiCard('CA moyen / station', `${fmt(kpis.ca_moyen_par_station)} FCFA`, `${kpis.nb_stations_actives} stations actives`, '#FF6B35')}
              </div>

              <div className="grid-2">
                <div className="card">
                  <h3>Tendance CA {annee}
                    <span style={{ fontSize: 13, fontWeight: 400, marginLeft: 10, color: tendances.tendance === 'hausse' ? '#78BE20' : '#e53935' }}>
                      {tendances.tendance === 'hausse' ? '▲ Hausse' : tendances.tendance === 'baisse' ? '▼ Baisse' : '— Stable'}
                      {' '}(R²={tendances.r_squared})
                    </span>
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={tendances.donnees}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mois_label" />
                      <YAxis tickFormatter={(v: any) => `${Math.round(v / 1000000)}M`} />
                      <Tooltip formatter={(v: any) => fmt(Number(v)) + ' FCFA'} />
                      <Legend />
                      <Area type="monotone" dataKey="ca" name="CA" stroke="#ED0000" fill="#ED000015" strokeWidth={2} />
                      <Line type="monotone" dataKey="ca_sma3" name="Moy. mobile (3m)" stroke="#004A97" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="card">
                  <h3>Indicateurs cles</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: '10px 0' }}>
                    <div style={{ textAlign: 'center', padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#004A97' }}>{kpis.nb_releves}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>Releves saisis</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#FF6B35' }}>{fmt(kpis.volume_moyen_par_jour)}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>Volume moyen / jour (L)</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: Number(kpis.ecart_total) < 0 ? '#e53935' : '#78BE20' }}>
                        {fmt(kpis.ecart_total)}
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>Ecart total (FCFA)</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#78BE20' }}>
                        {tendances.pente_mensuelle > 0 ? '+' : ''}{fmt(tendances.pente_mensuelle)}
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>Pente mensuelle (FCFA)</div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ===== PREVISIONS VENTES ===== */}
          {tab === 'previsions' && previsions && (
            <>
              {previsions.message ? (
                <div className="card" style={{ textAlign: 'center', padding: 40 }}>{previsions.message}</div>
              ) : (
                <>
                  <div className="stats-grid">
                    {renderKpiCard('Tendance', previsions.tendance === 'hausse' ? '▲ Hausse' : '▼ Baisse', `Pente: ${fmt(previsions.pente_mensuelle)} FCFA/mois`, previsions.tendance === 'hausse' ? '#78BE20' : '#e53935')}
                    {renderKpiCard('Fiabilite (R²)', `${Math.round(previsions.r_squared * 100)}%`, previsions.r_squared > 0.7 ? 'Bonne correlation' : 'Correlation faible', '#004A97')}
                    {previsions.previsions[0] && renderKpiCard(
                      `Prevision ${previsions.previsions[0].mois_label}`,
                      `${fmt(previsions.previsions[0].ca_regression)} FCFA`,
                      `[${fmt(previsions.previsions[0].ca_min)} - ${fmt(previsions.previsions[0].ca_max)}]`, '#FF6B35'
                    )}
                  </div>

                  <div className="card">
                    <h3>Historique & Previsions CA</h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={[
                        ...previsions.historique.map((h: any) => ({ ...h, type: 'historique' })),
                        ...previsions.previsions.map((p: any) => ({
                          mois_str: p.mois_str, mois_label: p.mois_label,
                          ca_regression: p.ca_regression, ca_min: p.ca_min, ca_max: p.ca_max,
                          type: 'prevision'
                        }))
                      ]}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="mois_label" />
                        <YAxis tickFormatter={(v: any) => `${Math.round(v / 1000000)}M`} />
                        <Tooltip formatter={(v: any) => fmt(Number(v)) + ' FCFA'} />
                        <Legend />
                        <Area dataKey="ca_max" name="Intervalle max" stroke="none" fill="#78BE2020" />
                        <Area dataKey="ca_min" name="Intervalle min" stroke="none" fill="#ffffff" />
                        <Line type="monotone" dataKey="ca" name="CA reel" stroke="#ED0000" strokeWidth={2} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="ca_sma" name="Moy. mobile" stroke="#004A97" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                        <Line type="monotone" dataKey="ca_regression" name="Prevision" stroke="#78BE20" strokeWidth={2.5} strokeDasharray="8 4" dot={{ r: 5, fill: '#78BE20' }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Forecast table */}
                  <div className="card" style={{ marginTop: 20 }}>
                    <h3>Detail des previsions</h3>
                    <table className="table">
                      <thead>
                        <tr><th>Mois</th><th style={{textAlign:'right'}}>CA prevu</th><th style={{textAlign:'right'}}>IC bas</th><th style={{textAlign:'right'}}>IC haut</th><th style={{textAlign:'right'}}>Volume prevu</th></tr>
                      </thead>
                      <tbody>
                        {previsions.previsions.map((p: any, i: number) => (
                          <tr key={i}>
                            <td><strong>{p.mois_label} {p.mois_str.slice(0,4)}</strong></td>
                            <td style={{textAlign:'right'}}>{fmt(p.ca_regression)} FCFA</td>
                            <td style={{textAlign:'right', color:'#999'}}>{fmt(p.ca_min)} FCFA</td>
                            <td style={{textAlign:'right', color:'#999'}}>{fmt(p.ca_max)} FCFA</td>
                            <td style={{textAlign:'right'}}>{fmt(p.volume_regression)} L</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}

          {/* ===== PREVISIONS STOCK ===== */}
          {tab === 'stock' && (
            <>
              <div className="stats-grid">
                {renderKpiCard('Total cuves', String(stockPrev.length), undefined, '#004A97')}
                {renderKpiCard('Critiques (<3j)', String(stockPrev.filter(s => s.severite === 'critical').length), undefined, '#e53935')}
                {renderKpiCard('Attention (<7j)', String(stockPrev.filter(s => s.severite === 'warning').length), undefined, '#FF6B35')}
                {renderKpiCard('OK', String(stockPrev.filter(s => s.severite === 'ok').length), undefined, '#78BE20')}
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>Station</th><th>Cuve</th><th>Produit</th><th>Niveau</th>
                    <th style={{textAlign:'right'}}>Conso/jour</th><th style={{textAlign:'right'}}>Jours restants</th>
                    <th>Date epuisement</th><th>Commander avant</th><th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {stockPrev.map((s: any) => (
                    <tr key={s.cuve_id} style={{ background: s.severite === 'critical' ? '#fff5f5' : s.severite === 'warning' ? '#fffbf0' : undefined }}>
                      <td><strong>{s.station_nom}</strong></td>
                      <td><code>{s.code}</code></td>
                      <td>{s.produit_nom}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{
                              width: `${s.taux_remplissage_pct}%`, height: '100%', borderRadius: 4,
                              background: s.taux_remplissage_pct < 15 ? '#e53935' : s.taux_remplissage_pct < 30 ? '#ff9800' : '#4caf50'
                            }} />
                          </div>
                          <span style={{ fontSize: 12, minWidth: 35 }}>{s.taux_remplissage_pct}%</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#999' }}>{fmt(s.niveau_actuel)} / {fmt(s.capacite)} L</div>
                      </td>
                      <td style={{textAlign:'right'}}>{fmt(s.consommation_moyenne_jour)} L</td>
                      <td style={{textAlign:'right'}}>
                        <span style={{ fontWeight: 700, fontSize: 16, color: s.severite === 'critical' ? '#e53935' : s.severite === 'warning' ? '#ff9800' : '#4caf50' }}>
                          {s.jours_restants !== null ? s.jours_restants : '—'}
                        </span>
                        {s.jours_restants !== null && <span style={{ fontSize: 11, color: '#999' }}> jours</span>}
                      </td>
                      <td>{s.date_epuisement_estimee ? new Date(s.date_epuisement_estimee).toLocaleDateString('fr-FR') : '—'}</td>
                      <td>{s.date_commande_suggeree ? <strong style={{ color: '#ED0000' }}>{new Date(s.date_commande_suggeree).toLocaleDateString('fr-FR')}</strong> : '—'}</td>
                      <td><span className={`badge badge-${s.severite === 'ok' ? 'success' : s.severite}`}>{s.severite}</span></td>
                    </tr>
                  ))}
                  {stockPrev.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 30 }}>Aucune donnee</td></tr>}
                </tbody>
              </table>
            </>
          )}

          {/* ===== ANOMALIES ===== */}
          {tab === 'anomalies' && anomalies && (
            <>
              <div className="stats-grid">
                {renderKpiCard('Anomalies detectees', String(anomalies.resume.total), undefined, '#FF6B35')}
                {renderKpiCard('Critiques (Z>3)', String(anomalies.resume.critiques), undefined, '#e53935')}
                {renderKpiCard('Warnings (Z>2)', String(anomalies.resume.warnings), undefined, '#FFC72C')}
              </div>

              {anomalies.anomalies.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: 40, color: '#78BE20' }}>
                  Aucune anomalie detectee pour cette periode
                </div>
              ) : (
                <table className="table">
                  <thead>
                    <tr><th>Type</th><th>Station</th><th>Date</th><th>Produit</th><th>Pompiste</th>
                      <th style={{textAlign:'right'}}>Valeur</th><th style={{textAlign:'right'}}>Moyenne</th>
                      <th style={{textAlign:'right'}}>Z-Score</th><th>Severite</th></tr>
                  </thead>
                  <tbody>
                    {anomalies.anomalies.map((a: any, i: number) => (
                      <tr key={i} style={{ background: a.severite === 'critical' ? '#fff5f5' : undefined }}>
                        <td>
                          <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: a.type === 'ecart_anormal' ? '#fef3f2' : a.type === 'volume_anormal_haut' ? '#f0fdf4' : '#fefce8', color: a.type === 'ecart_anormal' ? '#b91c1c' : a.type === 'volume_anormal_haut' ? '#166534' : '#854d0e' }}>
                            {a.type === 'ecart_anormal' ? 'Ecart' : a.type === 'volume_anormal_haut' ? 'Vol. haut' : 'Vol. bas'}
                          </span>
                        </td>
                        <td><strong>{a.station_nom}</strong></td>
                        <td>{new Date(a.date).toLocaleDateString('fr-FR')}</td>
                        <td>{a.produit}</td>
                        <td>{a.pompiste}</td>
                        <td style={{textAlign:'right', fontWeight: 600, color: a.type === 'ecart_anormal' ? '#e53935' : undefined}}>
                          {fmt(a.valeur)} {a.type === 'ecart_anormal' ? 'FCFA' : 'L'}
                        </td>
                        <td style={{textAlign:'right', color:'#999'}}>{fmt(a.moyenne)}</td>
                        <td style={{textAlign:'right'}}>
                          <span style={{ fontWeight: 700, color: Math.abs(a.z_score) > 3 ? '#e53935' : '#ff9800' }}>
                            {a.z_score > 0 ? '+' : ''}{a.z_score}
                          </span>
                        </td>
                        <td><span className={`badge badge-${a.severite}`}>{a.severite}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {/* ===== SAISONNALITE ===== */}
          {tab === 'saisonnalite' && saison && (
            <>
              {saison.insights && saison.insights.length > 0 && (
                <div className="card" style={{ marginBottom: 20, background: '#f0f7ff' }}>
                  <h3 style={{ color: '#004A97', marginBottom: 8 }}>Insights</h3>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {saison.insights.map((ins: string, i: number) => (
                      <li key={i} style={{ marginBottom: 4, color: '#333' }}>{ins}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid-2">
                <div className="card">
                  <h3>Activite par jour de semaine</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={saison.par_jour_semaine}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="jour" fontSize={12} />
                      <YAxis domain={[0, 'auto']} />
                      <Tooltip formatter={(v: any, name: any) => name === 'index' ? `${v}` : `${fmt(Number(v))}${String(name).includes('CA') ? ' FCFA' : ' L'}`} />
                      <ReferenceLine y={100} stroke="#999" strokeDasharray="3 3" label={{ value: 'Moyenne (100)', position: 'right', fontSize: 11 }} />
                      <Bar dataKey="index" name="Indice activite">
                        {saison.par_jour_semaine.map((_: any, i: number) => (
                          <Cell key={i} fill={saison.par_jour_semaine[i].index >= 100 ? '#78BE20' : '#FF6B35'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="card">
                  <h3>Activite par mois</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={saison.par_mois}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mois_label" fontSize={12} />
                      <YAxis domain={[0, 'auto']} />
                      <Tooltip formatter={(v: any, name: any) => name === 'index' ? `${v}` : `${fmt(Number(v))}${String(name).includes('CA') ? ' FCFA' : ' L'}`} />
                      <ReferenceLine y={100} stroke="#999" strokeDasharray="3 3" label={{ value: 'Moy.', position: 'right', fontSize: 11 }} />
                      <Bar dataKey="index" name="Indice activite">
                        {saison.par_mois.map((_: any, i: number) => (
                          <Cell key={i} fill={saison.par_mois[i].index >= 100 ? '#004A97' : '#FF6B35'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Detail tables */}
              <div className="grid-2" style={{ marginTop: 20 }}>
                <div className="card">
                  <h3>Detail par jour</h3>
                  <table className="table">
                    <thead><tr><th>Jour</th><th style={{textAlign:'right'}}>Vol. moyen</th><th style={{textAlign:'right'}}>CA moyen</th><th style={{textAlign:'right'}}>Indice</th></tr></thead>
                    <tbody>
                      {saison.par_jour_semaine.map((d: any) => (
                        <tr key={d.dow}>
                          <td><strong>{d.jour}</strong></td>
                          <td style={{textAlign:'right'}}>{fmt(d.volume_moyen)} L</td>
                          <td style={{textAlign:'right'}}>{fmt(d.ca_moyen)} FCFA</td>
                          <td style={{textAlign:'right'}}>
                            <span style={{ fontWeight: 600, color: d.index >= 100 ? '#78BE20' : '#FF6B35' }}>{d.index}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card">
                  <h3>Detail par mois</h3>
                  <table className="table">
                    <thead><tr><th>Mois</th><th style={{textAlign:'right'}}>Volume</th><th style={{textAlign:'right'}}>CA</th><th style={{textAlign:'right'}}>Indice</th></tr></thead>
                    <tbody>
                      {saison.par_mois.map((m: any) => (
                        <tr key={m.mois}>
                          <td><strong>{m.mois_label}</strong></td>
                          <td style={{textAlign:'right'}}>{fmt(m.volume)} L</td>
                          <td style={{textAlign:'right'}}>{fmt(m.ca)} FCFA</td>
                          <td style={{textAlign:'right'}}>
                            <span style={{ fontWeight: 600, color: m.index >= 100 ? '#004A97' : '#FF6B35' }}>{m.index}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ===== RECOMMANDATIONS ===== */}
          {tab === 'recommandations' && recos && (
            <>
              <div className="stats-grid">
                {renderKpiCard('Total recommandations', String(recos.resume.total), undefined, '#004A97')}
                {renderKpiCard('Priorite haute', String(recos.resume.haute), undefined, '#ED0000')}
                {renderKpiCard('Priorite moyenne', String(recos.resume.moyenne), undefined, '#FF6B35')}
                {renderKpiCard('Priorite basse', String(recos.resume.basse), undefined, '#78BE20')}
              </div>

              {recos.recommandations.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: 40, color: '#78BE20' }}>
                  Aucune recommandation - Tout est en ordre !
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 12 }}>
                  {recos.recommandations.map((r: any, i: number) => (
                    <div key={i} className="card" style={{ borderLeft: `4px solid ${PRIO_COLORS[r.priorite]}`, padding: '16px 20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          <span style={{ fontSize: 24 }}>{CAT_ICONS[r.categorie] || '📋'}</span>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{r.titre}</div>
                            <div style={{ color: '#666', fontSize: 13 }}>{r.description}</div>
                            <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                              <span className="badge badge-info">{r.categorie}</span>
                              <span style={{ fontSize: 12, color: '#999' }}>{r.station_nom}</span>
                            </div>
                          </div>
                        </div>
                        <span className={`badge badge-${r.priorite === 'haute' ? 'critical' : r.priorite === 'moyenne' ? 'warning' : 'success'}`}>
                          {r.priorite}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Analytics;
